import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Dot,
  Legend,
  Scatter,
  ComposedChart,
} from "recharts";

const SimpleCompactionChart = ({ data }) => {
  // Process the data to create chart-friendly format
  const chartData = useMemo(() => {
    const points = [];

    // Collect all data points
    for (let i = 1; i <= 5; i++) {
      const mcValue = parseFloat(data[`averageMc${i}`]);
      const dryUnitValue = parseFloat(data[`dryUnitWeight${i}`]);

      // Only include valid data points
      if (
        !isNaN(mcValue) &&
        !isNaN(dryUnitValue) &&
        mcValue !== "" &&
        dryUnitValue !== ""
      ) {
        points.push({
          mc: mcValue,
          dryUnit: dryUnitValue,
          index: i,
          pointName: `Point ${i}`,
        });
      }
    }

    return points.sort((a, b) => a.mc - b.mc);
  }, [data]);

  // Fit a quadratic curve (y = ax² + bx + c) to the data points
  const fitQuadraticCurve = (points) => {
    if (points.length < 3) return null;

    // Set up matrices for least squares quadratic fit
    const n = points.length;
    let sumX = 0,
      sumX2 = 0,
      sumX3 = 0,
      sumX4 = 0;
    let sumY = 0,
      sumXY = 0,
      sumX2Y = 0;

    points.forEach((point) => {
      const x = point.mc;
      const y = point.dryUnit;
      const x2 = x * x;

      sumX += x;
      sumX2 += x2;
      sumX3 += x2 * x;
      sumX4 += x2 * x2;
      sumY += y;
      sumXY += x * y;
      sumX2Y += x2 * y;
    });

    // Solve the normal equations for quadratic fit
    const matrix = [
      [n, sumX, sumX2],
      [sumX, sumX2, sumX3],
      [sumX2, sumX3, sumX4],
    ];

    const vector = [sumY, sumXY, sumX2Y];

    // Solve using Cramer's rule for 3x3 matrix
    const det = determinant(matrix);

    if (Math.abs(det) < 1e-10) return null;

    // Calculate coefficients
    const matrixC = [
      [vector[0], sumX, sumX2],
      [vector[1], sumX2, sumX3],
      [vector[2], sumX3, sumX4],
    ];

    const matrixB = [
      [n, vector[0], sumX2],
      [sumX, vector[1], sumX3],
      [sumX2, vector[2], sumX4],
    ];

    const matrixA = [
      [n, sumX, vector[0]],
      [sumX, sumX2, vector[1]],
      [sumX2, sumX3, vector[2]],
    ];

    const c = determinant(matrixC) / det;
    const b = determinant(matrixB) / det;
    const a = determinant(matrixA) / det;

    return { a, b, c };
  };

  const determinant = (matrix) => {
    return (
      matrix[0][0] *
        (matrix[1][1] * matrix[2][2] - matrix[1][2] * matrix[2][1]) -
      matrix[0][1] *
        (matrix[1][0] * matrix[2][2] - matrix[1][2] * matrix[2][0]) +
      matrix[0][2] * (matrix[1][0] * matrix[2][1] - matrix[1][1] * matrix[2][0])
    );
  };

  // Generate curve points from quadratic equation
  const curveData = useMemo(() => {
    if (chartData.length < 2) return [];

    const coefficients = fitQuadraticCurve(chartData);

    if (!coefficients) return [];

    const { a, b, c } = coefficients;

    // Generate points for smooth curve
    const points = [];
    const steps = 100;

    // Extend range slightly beyond data points for better curve visualization
    const minMc = Math.min(...chartData.map((p) => p.mc)) - 2;
    const maxMc = Math.max(...chartData.map((p) => p.mc)) + 2;

    for (let i = 0; i <= steps; i++) {
      const mc = minMc + (maxMc - minMc) * (i / steps);
      // Quadratic equation: y = ax² + bx + c
      const dryUnit = a * mc * mc + b * mc + c;

      points.push({
        mc,
        dryUnit: Math.max(0, dryUnit), // Ensure non-negative values
      });
    }

    return points;
  }, [chartData]);

  // Find vertex of parabola (maximum point)
  const vertex = useMemo(() => {
    if (chartData.length < 2) return null;

    const coefficients = fitQuadraticCurve(chartData);

    if (!coefficients) return null;

    const { a, b, c } = coefficients;

    // For a quadratic ax² + bx + c, vertex x = -b/(2a)
    const vertexX = -b / (2 * a);
    const vertexY = a * vertexX * vertexX + b * vertexX + c;

    return {
      mc: vertexX,
      dryUnit: vertexY,
    };
  }, [chartData]);

  // Custom scatter point renderer
  const renderScatterPoint = (props) => {
    const { cx, cy, payload } = props;

    // Check if this point is the vertex (within tolerance)
    const isVertex =
      vertex &&
      Math.abs(payload.mc - vertex.mc) < 0.5 &&
      Math.abs(payload.dryUnit - vertex.dryUnit) < 0.01;

    return (
      <g>
        <Dot
          cx={cx}
          cy={cy}
          r={isVertex ? 10 : 8}
          fill={isVertex ? "#ff6b6b" : "#4a90e2"}
          stroke="#fff"
          strokeWidth={3}
        />
        {/* Add point number near the point */}
        <text
          x={cx + 12}
          y={cy - 12}
          fill="#333"
          fontSize={11}
          fontWeight="bold"
          style={{ textShadow: "1px 1px 0 white" }}
        >
          {payload.pointName}
        </text>
      </g>
    );
  };

  if (chartData.length === 0) {
    return (
      <div
        style={{
          padding: "20px",
          textAlign: "center",
          color: "#666",
          border: "1px dashed #ccc",
          borderRadius: "4px",
        }}
      >
        No valid data points available for chart
      </div>
    );
  }

  return (
    <div style={{ width: "100%", padding: "20px" }}>
      <div style={{ width: "100%", height: 500 }}>
        <ResponsiveContainer>
          <ComposedChart margin={{ top: 50, right: 30, left: 60, bottom: 40 }}>
            {/* Enhanced grid lines - more visible */}
            <CartesianGrid
              strokeDasharray="5 5"
              stroke="#666"
              strokeWidth={1.5}
              vertical={true}
              horizontal={true}
            />

            {/* Secondary lighter grid for better readability */}
            <CartesianGrid
              strokeDasharray="2 2"
              stroke="#ccc"
              strokeWidth={0.8}
              vertical={true}
              horizontal={true}
            />

            {/* X Axis */}
            <XAxis
              dataKey="mc"
              type="number"
              domain={["auto", "auto"]}
              label={{
                value: data.averageMcText || "Average M.C. (%)",
                position: "bottom",
                offset: 20,
                style: { fontSize: 14, fill: "#333", fontWeight: "bold" },
              }}
              tick={{ fill: "#333", fontSize: 12, fontWeight: "bold" }}
              tickLine={{ stroke: "#333", strokeWidth: 1.5 }}
              axisLine={{ stroke: "#333", strokeWidth: 2 }}
              allowDataOverflow={true}
            />

            {/* Y Axis */}
            <YAxis
              yAxisId="left"
              type="number"
              domain={["auto", "auto"]}
              label={{
                value: data.dryUnitWeightText || "Dry Unit Weight (g/cm³)",
                angle: -90,
                position: "left",
                offset: 40,
                style: { fontSize: 14, fill: "#333", fontWeight: "bold" },
              }}
              tick={{ fill: "#333", fontSize: 12, fontWeight: "bold" }}
              tickLine={{ stroke: "#333", strokeWidth: 1.5 }}
              axisLine={{ stroke: "#333", strokeWidth: 2 }}
            />

            {/* Tooltip */}
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const dataPoint = payload[0].payload;

                  return (
                    <div
                      style={{
                        backgroundColor: "#fff",
                        border: "2px solid #333",
                        borderRadius: "4px",
                        padding: "10px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        fontSize: "13px",
                      }}
                    >
                      <p
                        style={{ margin: 0, color: "#333", fontWeight: "bold" }}
                      >
                        {dataPoint.pointName || "Curve Point"}
                      </p>
                      <p style={{ margin: "5px 0 0", color: "#333" }}>
                        <strong>M.C.:</strong>{" "}
                        {dataPoint.mc?.toFixed(2) || "N/A"}%
                      </p>
                      <p style={{ margin: "5px 0 0", color: "#333" }}>
                        <strong>Dry Unit Weight:</strong>{" "}
                        {dataPoint.dryUnit?.toFixed(3) || "N/A"} g/cm³
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />

            {/* Legend */}
            <Legend
              verticalAlign="top"
              height={36}
              payload={[
                {
                  value: "Fitted Curve (Quadratic)",
                  type: "line",
                  color: "#82ca9d",
                },
                {
                  value: "Original Data Points",
                  type: "circle",
                  color: "#4a90e2",
                },
                { value: "Maximum Point", type: "circle", color: "#ff6b6b" },
              ]}
              wrapperStyle={{ fontSize: "13px", fontWeight: "bold" }}
            />

            {/* The parabolic curve */}
            <Line
              yAxisId="left"
              type="monotone"
              data={curveData}
              dataKey="dryUnit"
              stroke="#82ca9d"
              strokeWidth={4}
              dot={false}
              activeDot={{
                r: 8,
                fill: "#82ca9d",
                stroke: "#fff",
                strokeWidth: 2,
              }}
              isAnimationActive={true}
              animationDuration={500}
              name="Fitted Curve"
            />

            {/* Original data points as scatter */}
            <Scatter
              yAxisId="left"
              data={chartData}
              shape={renderScatterPoint}
              fill="#4a90e2"
              name="Original Data"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Statistics and Data Points Table */}
      <div
        style={{
          marginTop: "30px",
          padding: "20px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #dee2e6",
        }}
      >
        {/* Equation and vertex info */}
        <div
          style={{
            marginBottom: "20px",
            padding: "15px",
            backgroundColor: "#fff",
            borderRadius: "6px",
            border: "1px solid #ced4da",
          }}
        >
          {(() => {
            const coefficients = fitQuadraticCurve(chartData);
            if (coefficients) {
              const { a, b, c } = coefficients;
              return (
                <div
                  style={{
                    fontWeight: "bold",
                    color: "#333",
                    fontSize: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Curve Equation: y = {a.toFixed(4)}x² {b >= 0 ? "+" : "-"}{" "}
                  {Math.abs(b).toFixed(4)}x {c >= 0 ? "+" : "-"}{" "}
                  {Math.abs(c).toFixed(4)}
                </div>
              );
            }
            return null;
          })()}

          <div style={{ display: "flex", gap: "30px", flexWrap: "wrap" }}>
            {vertex && vertex.mc > 0 && vertex.dryUnit > 0 && (
              <>
                <div style={{ fontSize: "15px" }}>
                  <span style={{ fontWeight: "bold", color: "#ff6b6b" }}>
                    Optimum Moisture Content:
                  </span>{" "}
                  {vertex.mc.toFixed(2)}%
                </div>
                <div style={{ fontSize: "15px" }}>
                  <span style={{ fontWeight: "bold", color: "#ff6b6b" }}>
                    Maximum Dry Unit Weight:
                  </span>{" "}
                  {vertex.dryUnit.toFixed(3)} g/cm³
                </div>
              </>
            )}
          </div>
        </div>

        {/* Data Points Table */}
        <div>
          <h3 style={{ margin: "0 0 15px 0", color: "#333", fontSize: "18px" }}>
            Original Data Points
          </h3>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              backgroundColor: "#fff",
              borderRadius: "6px",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#4a90e2", color: "white" }}>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    borderBottom: "2px solid #357abd",
                  }}
                >
                  Point
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    borderBottom: "2px solid #357abd",
                  }}
                >
                  Moisture Content (%)
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    borderBottom: "2px solid #357abd",
                  }}
                >
                  Dry Unit Weight (g/cm³)
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    borderBottom: "2px solid #357abd",
                  }}
                >
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {chartData.map((point, index) => {
                const isMaxPoint =
                  vertex &&
                  Math.abs(point.mc - vertex.mc) < 0.5 &&
                  Math.abs(point.dryUnit - vertex.dryUnit) < 0.01;

                return (
                  <tr
                    key={index}
                    style={{
                      backgroundColor: isMaxPoint
                        ? "#fff3f3"
                        : index % 2 === 0
                          ? "#f8f9fa"
                          : "#fff",
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    <td style={{ padding: "10px", fontWeight: "bold" }}>
                      {point.pointName}
                    </td>
                    <td style={{ padding: "10px" }}>{point.mc.toFixed(2)}%</td>
                    <td style={{ padding: "10px" }}>
                      {point.dryUnit.toFixed(3)} g/cm³
                    </td>
                    <td style={{ padding: "10px" }}>
                      {isMaxPoint ? (
                        <span
                          style={{
                            backgroundColor: "#ff6b6b",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "bold",
                          }}
                        >
                          Maximum Point
                        </span>
                      ) : (
                        <span style={{ color: "#6c757d" }}>Regular Point</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Summary */}
          <div
            style={{
              marginTop: "15px",
              padding: "10px",
              backgroundColor: "#e9ecef",
              borderRadius: "4px",
              fontSize: "14px",
              color: "#495057",
            }}
          >
            <strong>Total Points:</strong> {chartData.length} |
            <strong> Min MC:</strong>{" "}
            {Math.min(...chartData.map((p) => p.mc)).toFixed(2)}% |
            <strong> Max MC:</strong>{" "}
            {Math.max(...chartData.map((p) => p.mc)).toFixed(2)}% |
            <strong> Min Dry Unit:</strong>{" "}
            {Math.min(...chartData.map((p) => p.dryUnit)).toFixed(3)} g/cm³ |
            <strong> Max Dry Unit:</strong>{" "}
            {Math.max(...chartData.map((p) => p.dryUnit)).toFixed(3)} g/cm³
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleCompactionChart;
